import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { Card, Stat, Btn, Badge, TableWrap, QrModal, ExportMenu } from "../shared/components"
import {
  submitFeedback,
  getMyFeedback,
  listFeedback,
  downloadFeedbackExport,
  getFeedbackQr,
} from "../api/feedback.api"

const CATEGORIES = ["GENERAL", "CONTENT", "TRAINER", "PLATFORM", "SUGGESTION"]
// "What worked well" chip keys — kept in sync with the backend ASPECTS list.
const ASPECTS = [
  { k: "content", l: "fbAspectContent" },
  { k: "trainer", l: "fbAspectTrainer" },
  { k: "pace", l: "fbAspectPace" },
  { k: "materials", l: "fbAspectMaterials" },
  { k: "drills", l: "fbAspectDrills" },
  { k: "venue", l: "fbAspectVenue" },
  { k: "discussion", l: "fbAspectDiscussion" },
]

function catLabel(t, c) {
  return t[`fbCat${c}`] || c
}

/**
 * Simpan blob sebagai berkas unduhan. Nama berkasnya ditentukan SERVER lewat
 * Content-Disposition, dan itulah yang dipakai peramban selama atribut
 * download di sini dikosongkan — sehingga nama berkas tidak perlu disusun dua
 * kali di dua tempat yang bisa berbeda. Pola yang sama dengan AppraisalScreens.
 */
async function unduh(promise, namaCadangan) {
  const res = await promise
  const url = URL.createObjectURL(res.blob)
  const a = document.createElement("a")
  a.href = url
  a.download = res.filename || namaCadangan
  a.click()
  URL.revokeObjectURL(url)
}

// ── Star rating input ────────────────────────────────────────
function StarPicker({ value, onChange, disabled }) {
  return (
    <div className="row gap-1" style={{ alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(value === n ? null : n)}
          style={{ background: "none", border: "none", cursor: disabled ? "default" : "pointer", padding: 2, lineHeight: 0 }}
          title={`${n}/5`}
        >
          <Icon
            name="star"
            size={22}
            color={n <= (value || 0) ? "var(--amber, #F59E0B)" : "var(--border-strong, #cbd5e1)"}
          />
        </button>
      ))}
      {value != null && <span className="mono muted" style={{ fontSize: 12, marginLeft: 4 }}>{value}/5</span>}
    </div>
  )
}

// ── Participant view: submit form + own history ──────────────
function ParticipantFeedback() {
  const t = useT()
  const { lang, currentProgramId, currentProgram } = useApp()
  const isMobile = useIsMobile()
  const [category, setCategory] = React.useState("GENERAL")
  const [rating, setRating] = React.useState(null)
  const [nps, setNps] = React.useState(null)
  const [aspects, setAspects] = React.useState([])
  const [aiTopic, setAiTopic] = React.useState("")
  const [subject, setSubject] = React.useState("")
  const [message, setMessage] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const [mine, setMine] = React.useState([])

  const toggleAspect = (k) =>
    setAspects((prev) => (prev.includes(k) ? prev.filter((a) => a !== k) : [...prev, k]))

  const reloadMine = React.useCallback(() => {
    if (!currentProgramId) { setMine([]); return }
    getMyFeedback(currentProgramId).then(setMine).catch(() => setMine([]))
  }, [currentProgramId])

  React.useEffect(() => { reloadMine() }, [reloadMine])

  const submit = async (e) => {
    e.preventDefault()
    if (!message.trim() || !currentProgramId) return
    setBusy(true)
    try {
      await submitFeedback(currentProgramId, {
        category,
        rating: rating || undefined,
        nps: nps != null ? nps : undefined,
        aspects: aspects.length ? aspects : undefined,
        ai_topic: aiTopic.trim() || undefined,
        subject: subject.trim() || undefined,
        message: message.trim(),
      })
      toast.success(t.fbThanks)
      // Lanjut ke kategori berikutnya, bukan reset ke General — peserta yang
      // ingin memberi feedback beberapa kategori berturut-turut tidak perlu
      // memilih ulang tiap kali. Mentok di Suggestion (kategori terakhir),
      // tidak berputar balik ke General.
      const idxBerikutnya = Math.min(CATEGORIES.indexOf(category) + 1, CATEGORIES.length - 1)
      setCategory(CATEGORIES[idxBerikutnya])
      setRating(null)
      setNps(null)
      setAspects([])
      setAiTopic("")
      setSubject("")
      setMessage("")
      reloadMine()
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally {
      setBusy(false)
    }
  }

  const inputStyle = { height: 36, padding: "0 10px", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)", fontSize: 13, width: "100%" }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          {currentProgram && <div className="eyebrow">{(currentProgram.code || "").toUpperCase()} · {currentProgram.name}</div>}
          <h1 className="h1" style={{ marginTop: currentProgram ? 6 : 0 }}>{t.fbTitle}</h1>
          <div className="muted mt-1">{t.fbDescParticipant}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: 16 }}>
        {/* Form */}
        <Card eyebrow={t.fbEyebrow} title={t.fbSend}>
          <form onSubmit={submit} className="col gap-3">
            <label className="col gap-1">
              <span className="small">{t.fbCategory}</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={busy} style={inputStyle}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(t, c)}</option>)}
              </select>
            </label>

            <div className="col gap-1">
              <span className="small">{t.fbRating}</span>
              <StarPicker value={rating} onChange={setRating} disabled={busy} />
            </div>

            <div className="col gap-1">
              <span className="small">{t.fbAspects} <span className="muted">{t.fbAspectsHint}</span></span>
              <div className="row gap-2" style={{ flexWrap: "wrap", marginTop: 2 }}>
                {ASPECTS.map((a) => {
                  const on = aspects.includes(a.k)
                  return (
                    <button
                      key={a.k}
                      type="button"
                      onClick={() => toggleAspect(a.k)}
                      disabled={busy}
                      style={{
                        padding: "6px 12px", borderRadius: 999, fontSize: 12, cursor: busy ? "default" : "pointer",
                        border: on ? "1px solid var(--program-color)" : "1px solid var(--border)",
                        background: on ? "var(--program-color-soft)" : "var(--surface)",
                        color: on ? "var(--program-color-text)" : "var(--text-2)",
                        fontWeight: on ? 500 : 400, display: "inline-flex", alignItems: "center", gap: 4,
                      }}
                    >
                      {on && <Icon name="check" size={12} color="var(--program-color-text)" />}{t[a.l]}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="col gap-1">
              <span className="small">{t.fbNps}</span>
              <div className="row gap-1" style={{ flexWrap: "wrap", marginTop: 2 }}>
                {Array.from({ length: 11 }, (_, i) => i).map((n) => {
                  const on = nps === n
                  const color = n <= 6 ? "var(--danger, #C62828)" : n <= 8 ? "var(--amber, #F59E0B)" : "var(--success, #2E7D32)"
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setNps(on ? null : n)}
                      disabled={busy}
                      style={{
                        width: 34, height: 34, borderRadius: 8, fontSize: 13, fontWeight: 500, fontFamily: "var(--font-mono)",
                        cursor: busy ? "default" : "pointer",
                        border: on ? `1.5px solid ${color}` : "1px solid var(--border)",
                        background: on ? color : "var(--surface)", color: on ? "white" : "var(--text)",
                      }}
                    >{n}</button>
                  )
                })}
              </div>
              <div className="row between" style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                <span>{t.fbNpsLow}</span><span>{t.fbNpsHigh}</span>
              </div>
            </div>

            <label className="col gap-1">
              <span className="small">{t.fbSubject}</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} placeholder={t.fbSubjectPlaceholder} maxLength={200} style={inputStyle} />
            </label>

            <label className="col gap-1">
              <span className="small">{t.fbMessage}</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={busy}
                required
                placeholder={t.fbMessagePlaceholder}
                rows={5}
                maxLength={5000}
                style={{ ...inputStyle, height: "auto", padding: "8px 10px", resize: "vertical", fontFamily: "inherit" }}
              />
            </label>

            <label className="col gap-1">
              <span className="small">{t.fbAiTopic}</span>
              <input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} disabled={busy} placeholder={t.fbAiTopicPlaceholder} maxLength={200} style={inputStyle} />
            </label>

            <div style={{ padding: 12, background: "var(--surface-2)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-md)", fontSize: 12, color: "var(--text-3)", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Icon name="shield" size={14} color="var(--text-3)" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>{t.fbPrivacy}</div>
            </div>

            <div className="row" style={{ justifyContent: "flex-end" }}>
              <Btn variant="primary" type="submit" icon={<Icon name="send" size={14} />} disabled={busy || !message.trim() || !currentProgramId}>
                {busy ? t.fbSending : t.fbSend}
              </Btn>
            </div>
          </form>
        </Card>

        {/* Own history */}
        <Card title={t.fbMineTitle} action={<Badge>{mine.length}</Badge>}>
          {mine.length === 0 ? (
            <div className="empty small">{t.fbNoneMine}</div>
          ) : (
            <div className="col gap-3">
              {mine.map((f) => (
                <div key={f.id} style={{ borderBottom: "1px solid var(--border-subtle)", paddingBottom: 10 }}>
                  <div className="row between" style={{ alignItems: "center" }}>
                    <Badge tone="brand">{catLabel(t, f.category)}</Badge>
                    <span className="muted" style={{ fontSize: 11 }}>
                      {new Date(f.createdAt).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  {f.subject && <div style={{ fontWeight: 500, fontSize: 13, marginTop: 6 }}>{f.subject}</div>}
                  <div className="muted" style={{ fontSize: 12, marginTop: 4, whiteSpace: "pre-wrap" }}>{f.message}</div>
                  {f.rating != null && (
                    <div className="row gap-1 mt-2" style={{ alignItems: "center" }}>
                      <Icon name="star" size={13} color="var(--amber, #F59E0B)" />
                      <span className="mono muted" style={{ fontSize: 11 }}>{f.rating}/5</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

// ── Admin view: list + summary + CSV/Excel/PDF export ─────────
function AdminFeedback() {
  const t = useT()
  const { lang, currentProgramId, currentProgram } = useApp()
  const isMobile = useIsMobile()
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [filter, setFilter] = React.useState("ALL")
  const [sedangUnduh, setSedangUnduh] = React.useState(null)
  const [qrOpen, setQrOpen] = React.useState(false)

  React.useEffect(() => {
    if (!currentProgramId) { setData(null); return }
    setLoading(true)
    listFeedback(currentProgramId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [currentProgramId])

  const handleExport = async (kind) => {
    if (!currentProgramId) return
    const format = kind.split("-").pop()
    setSedangUnduh(kind)
    try {
      await unduh(
        downloadFeedbackExport(currentProgramId, format),
        `feedback-${currentProgram?.code || "program"}.${format}`
      )
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setSedangUnduh(null)
    }
  }

  const items = data?.items || []
  const shown = filter === "ALL" ? items : items.filter((f) => f.category === filter)
  const summary = data?.summary

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          {currentProgram && <div className="eyebrow">{(currentProgram.code || "").toUpperCase()} · {currentProgram.name}</div>}
          <h1 className="h1" style={{ marginTop: currentProgram ? 6 : 0 }}>{t.fbTitle}</h1>
          <div className="muted mt-1">{t.fbDescAdmin}</div>
        </div>
        <div className="page-header-actions">
          {/* Tidak digerbangi apa pun, beda dari Editor Tes yang menyembunyikan
              tombolnya saat tesnya belum dibuat: form feedback selalu ada dan
              selalu bisa diisi, jadi QR-nya tidak pernah menuju layar kosong. */}
          <Btn variant="secondary" icon={<Icon name="qr" size={14} />} onClick={() => setQrOpen(true)}>
            {lang === "id" ? "Tampilkan QR" : "Show QR"}
          </Btn>
          <ExportMenu
            formats={["csv", "xlsx", "pdf"]}
            kindPrefix="feedback"
            busyKind={sedangUnduh}
            lang={lang}
            label={t.fbExport}
            variant="primary"
            onSelect={handleExport}
          />
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Stat label={t.fbAdminTotal} value={String(summary?.total ?? 0)} />
        <Stat
          label={t.fbAdminAvgRating}
          value={summary?.avgRating != null ? summary.avgRating.toFixed(1) : "—"}
          sub={`${summary?.ratingCount ?? 0} ${t.fbAdminRatingsWord}`}
        />
        <Stat
          label={t.fbAdminNps}
          value={summary?.npsScore != null ? String(summary.npsScore) : "—"}
          sub={`${summary?.npsCount ?? 0} ${t.fbAdminRatingsWord}`}
        />
        <Stat
          label={t.fbAdminPromoters}
          value={String(summary?.promoters ?? 0)}
          sub={`${summary?.detractors ?? 0} ${t.fbAdminDetractors}`}
        />
      </div>

      {/* Aspect breakdown */}
      {summary?.byAspect && Object.values(summary.byAspect).some((v) => v > 0) && (
        <div className="row gap-2 mb-3" style={{ flexWrap: "wrap", alignItems: "center" }}>
          <span className="eyebrow" style={{ marginRight: 4 }}>{t.fbAspectsTitle}</span>
          {ASPECTS.filter((a) => (summary.byAspect[a.k] || 0) > 0)
            .sort((a, b) => summary.byAspect[b.k] - summary.byAspect[a.k])
            .map((a) => (
              <Badge key={a.k} tone="brand">{t[a.l]} · {summary.byAspect[a.k]}</Badge>
            ))}
        </div>
      )}

      {/* Category filter */}
      <div className="row gap-2 mb-3" style={{ flexWrap: "wrap" }}>
        {["ALL", ...CATEGORIES].map((c) => {
          const count = c === "ALL" ? items.length : (summary?.byCategory?.[c] ?? 0)
          const active = filter === c
          return (
            <button
              key={c}
              className={`btn btn-sm ${active ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setFilter(c)}
            >
              {c === "ALL" ? (lang === "id" ? "Semua" : "All") : catLabel(t, c)} ({count})
            </button>
          )
        })}
      </div>

      <Card noPad>
        {loading ? (
          <div className="card-body"><div className="muted small" style={{ textAlign: "center" }}>{t.loadingText}</div></div>
        ) : shown.length === 0 ? (
          <div className="card-body"><div className="empty small">{t.fbNoFeedback}</div></div>
        ) : (
          <TableWrap>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t.fbColDate}</th>
                  <th>{t.fbColParticipant}</th>
                  <th>{t.fbColCategory}</th>
                  <th>{t.fbColRating}</th>
                  <th>NPS</th>
                  <th>{t.fbColFeedback}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((f) => (
                  <tr key={f.id}>
                    <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {new Date(f.createdAt).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 12 }}>{f.participant}</div>
                      {f.email && <div className="muted" style={{ fontSize: 11 }}>{f.email}</div>}
                    </td>
                    <td><Badge tone="brand">{catLabel(t, f.category)}</Badge></td>
                    <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {f.rating != null ? `${f.rating}/5` : "—"}
                    </td>
                    <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {f.nps != null ? `${f.nps}/10` : "—"}
                    </td>
                    <td style={{ maxWidth: 420 }}>
                      {f.subject && <div style={{ fontWeight: 500, fontSize: 12 }}>{f.subject}</div>}
                      <div className="muted" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{f.message}</div>
                      {Array.isArray(f.aspects) && f.aspects.length > 0 && (
                        <div className="row gap-1 mt-1" style={{ flexWrap: "wrap" }}>
                          {f.aspects.map((k) => {
                            const a = ASPECTS.find((x) => x.k === k)
                            return <Badge key={k} tone="outline">{a ? t[a.l] : k}</Badge>
                          })}
                        </div>
                      )}
                      {f.ai_topic && (
                        <div className="tiny" style={{ marginTop: 4, color: "var(--program-color)" }}>
                          <Icon name="ai" size={11} /> {f.ai_topic}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      {qrOpen && <FeedbackQrModal programId={currentProgramId} onClose={() => setQrOpen(false)} />}
    </div>
  )
}


// Pengambil datanya di sini, tampilannya di QrModal bersama — Editor Tes
// memakai modal yang sama untuk QR pretest/posttest.
function FeedbackQrModal({ programId, onClose }) {
  const { lang } = useApp()
  const [data, setData] = React.useState(null)
  const [gagal, setGagal] = React.useState(null)

  React.useEffect(() => {
    let batal = false
    getFeedbackQr(programId)
      .then((d) => { if (!batal) setData(d) })
      .catch((e) => { if (!batal) setGagal(e?.response?.data?.error || e.message) })
    return () => { batal = true }
  }, [programId])

  return (
    <QrModal
      title={lang === "id" ? "QR Form Feedback" : "Feedback Form QR"}
      url={data?.url}
      qrPngDataUrl={data?.qrPngDataUrl}
      downloadName="qr-feedback.png"
      error={gagal}
      onClose={onClose}
      lang={lang}
    />
  )
}

export default function FeedbackScreen() {
  const t = useT()
  const { role } = useApp()
  if (role === "participant") return <ParticipantFeedback />
  if (role === "admin") return <AdminFeedback />
  // Trainers and any other role have no access to feedback.
  return (
    <div className="main-inner">
      <div className="tile mt-5 tile-pad" style={{ display: "grid", placeItems: "center", height: 200, color: "var(--text-tertiary)" }}>
        {t.fbAdminOnly}
      </div>
    </div>
  )
}
